import { inject as injectBranding } from '@angular/core';
import { SiteBrandingService } from '../../../../services/site-branding.service';
import { CommonModule } from '@angular/common';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';

import { FormsModule } from '@angular/forms';

import {
  DeleteRequest,
  Location
} from '../../../../models/app.models';

import { LocationService } from '../../../../services/location.service';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-admin-locations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-locations.component.html',
  styleUrls: [
    './admin-locations.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminLocationsComponent
  implements OnInit {
  readonly branding = injectBranding(SiteBrandingService);

  readonly requiredFieldsMessage =
    'Complete all required fields.';

  locations: Location[] = [];

  locationsLoading = true;

  locationEditorOpen = false;

  isSavingLocation = false;

  locationDraft: Location =
    this.emptyLocation();

  deleteRequest: DeleteRequest | null = null;

  constructor(
    private readonly locationService:
      LocationService,
    private readonly toastService:
      ToastService,
    private readonly changeDetector:
      ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadLocations();
  }

  get isRetrievingCoordinates(): boolean {
    return (
      Number(this.locationDraft.latitude) === 0 &&
      Number(this.locationDraft.longitude) === 0
    );
  }

  loadLocations(): void {
    this.locationsLoading = true;

    this.locationService
      .getAdminLocations()
      .subscribe({
        next: locations => {
          this.locations = locations;

          this.locationsLoading = false;

          this.changeDetector.detectChanges();
        },

        error: error => {
          console.error(
            'Unable to load locations:',
            error
          );

          this.locationsLoading = false;

          this.toastService.error(
            'Unable to load locations.'
          );

          this.changeDetector.detectChanges();
        }
      });
  }

  openLocationEditor(
    location?: Location
  ): void {
    this.locationDraft = location
      ? { ...location }
      : this.emptyLocation();

    this.locationEditorOpen = true;

    this.changeDetector.detectChanges();
  }

  closeLocationEditor(): void {
    if (this.isSavingLocation) {
      return;
    }

    this.locationDraft =
      this.emptyLocation();

    this.locationEditorOpen = false;

    this.changeDetector.detectChanges();
  }

  saveLocation(): void {
    if (
      !this.locationDraft.name.trim() ||
      !this.locationDraft.address.trim() ||
      !this.locationDraft.phone.trim() ||
      !this.locationDraft.hours.trim()
    ) {
      this.toastService.error(
        this.requiredFieldsMessage
      );

      return;
    }

    this.isSavingLocation = true;

    this.changeDetector.detectChanges();

    const request =
      this.locationDraft.id
        ? this.locationService.updateLocation(
            this.locationDraft
          )
        : this.locationService.createLocation(
            this.locationDraft
          );

    request.subscribe({
      next: savedLocation => {
        const alreadyExists =
          this.locations.some(
            location =>
              location.id === savedLocation.id
          );

        this.locations = alreadyExists
          ? this.locations.map(location =>
              location.id === savedLocation.id
                ? savedLocation
                : location
            )
          : [
              ...this.locations,
              savedLocation
            ];

        this.locations.sort(
          (a, b) =>
            a.displayOrder - b.displayOrder ||
            a.name.localeCompare(b.name)
        );

        this.isSavingLocation = false;

        this.locationEditorOpen = false;

        this.locationDraft =
          this.emptyLocation();

        this.toastService.success(
          'Location saved.'
        );

        this.changeDetector.detectChanges();
      },

      error: error => {
        console.error(
          'Unable to save location:',
          error
        );

        this.isSavingLocation = false;

        this.toastService.error(
          'Unable to save location.'
        );

        this.changeDetector.detectChanges();
      }
    });
  }

  requestDelete(
    kind: DeleteRequest['kind'],
    id: number,
    label: string
  ): void {
    this.deleteRequest = {
      kind,
      id,
      label
    };

    this.changeDetector.detectChanges();
  }

  confirmDelete(): void {
    if (!this.deleteRequest) {
      return;
    }

    this.deleteLocation(
      this.deleteRequest.id
    );
  }

  cancelDelete(): void {
    this.deleteRequest = null;

    this.changeDetector.detectChanges();
  }

  private deleteLocation(
    id: number
  ): void {
    this.locationService
      .deleteLocation(id)
      .subscribe({
        next: () => {
          this.locations =
            this.locations.filter(
              location =>
                location.id !== id
            );

          this.deleteRequest = null;

          this.toastService.success(
            'Location deleted.'
          );

          this.changeDetector.detectChanges();
        },

        error: error => {
          console.error(
            'Unable to delete location:',
            error
          );

          this.toastService.error(
            'Unable to delete location.'
          );

          this.changeDetector.detectChanges();
        }
      });
  }

  trackById(
    _: number,
    item: { id: number }
  ): number {
    return item.id;
  }

  private emptyLocation(): Location {
    return {
      id: 0,
      name: '',
      address: '',
      phone: '',
      hours: '',
      latitude: 0,
      longitude: 0,
      displayOrder: 0,
      isActive: true
    };
  }
}
